/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import Text from 'components/shared/Text';
import { useIsMobile } from 'helpers/hooks';
import styles from './styles.module.scss';

function Field(props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [inputFocused, setInputFocused] = useState(false);

  const handleFocus = () => {
    setInputFocused(true);
  };

  const handleBlur = e => {
    setInputFocused(false);
    if (typeof props.onBlur === 'function') {
      props.onBlur(e);
    }
  };

  return (
    <div
      className={classnames(
        styles.fieldWrapper,
        {
          [styles.fieldWrapperMarginBottom]: !props.ignoreMarginBottom,
          [styles.fieldWrapperIgnoreMarginBottom]: props.ignoreMarginBottom
        },
        [...props.fieldWrapperStyles]
      )}>
      {!props.noLabel && (
        <Text
          className={classnames([
            styles.fieldLabel,
            { [styles.fieldWithContentLabel]: props.value.length > 0 || props.hasContent }
          ])}
          tag="label"
          htmlFor={props.id}>
          {props.name}
        </Text>
      )}
      <div
        className={classnames([
          styles.inputWrapper,
          {
            [styles.focused]: inputFocused,
            [styles.error]: props.error,
            [styles.orangeBorder]: props.passwordStrength === 3,
            [styles.greenBorder]: props.passwordStrength > 3,
            [styles.smallSize]: props.size === 'small',
            [styles.mediumSize]: props.size === 'medium',
            [styles.fullWidth]: !props.size || props.size === 'fullWidth'
          },
          ...props.inputWrapperStyles
        ])}>
        <input
          className={classnames([styles.fieldInput, ...props.inputStyles])}
          id={props.id}
          placeholder={props.placeholder ?? (isMobile ? 'Type' : undefined)}
          type={props.type}
          pattern={props.pattern}
          value={props.value}
          onChange={props.onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={props.maxLength}
          autoFocus={props.autoFocus}
          autoComplete={props.autoComplete}
        />
        {props.showPasswordStrength && (
          <div className={styles.strengthMeterContainer}>
            <div
              className={classnames([
                styles.strengthMeter,
                {
                  [styles.compromised]: props.passwordCompromised,
                  [styles.weak]: props.passwordStrength <= 2,
                  [styles.medium]: props.passwordStrength === 3,
                  [styles.quarter]:
                    props.passwordCompromised || Math.max(25, 25 * props.passwordStrength) === 25,
                  [styles.half]: Math.max(25, 25 * props.passwordStrength) === 50,
                  [styles.threeQuarters]: Math.max(25, 25 * props.passwordStrength) === 75,
                  [styles.empty]: props.value.length === 0
                }
              ])}
            />
          </div>
        )}
        {props.inlineElements ?? null}
      </div>
      {props.error && !props.noErrorLabel && (
        <Text tag="p" styles={[styles.fieldError]}>
          {t(props.error)}
        </Text>
      )}
    </div>
  );
}

Field.propTypes = {
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.string,
  error: PropTypes.string,
  id: PropTypes.string.isRequired,
  noLabel: PropTypes.bool,
  noErrorLabel: PropTypes.bool,
  maxLength: PropTypes.number,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  passwordCompromised: PropTypes.bool,
  passwordStrength: PropTypes.number,
  showPasswordStrength: PropTypes.bool,
  inlineElements: PropTypes.element,
  type: PropTypes.string,
  pattern: PropTypes.string,
  value: PropTypes.string.isRequired,
  fieldWrapperStyles: PropTypes.array,
  inputWrapperStyles: PropTypes.array,
  inputStyles: PropTypes.array,
  hasContent: PropTypes.bool,
  placeholder: PropTypes.string,
  ignoreMarginBottom: PropTypes.bool,
  size: PropTypes.string
};

Field.defaultProps = {
  autoFocus: false,
  autoComplete: 'off',
  noLabel: undefined,
  noErrorLabel: undefined,
  error: undefined,
  maxLength: undefined,
  onBlur: undefined,
  passwordCompromised: false,
  passwordStrength: 0,
  showPasswordStrength: false,
  inlineElements: undefined,
  type: 'text',
  pattern: undefined,
  fieldWrapperStyles: [],
  inputWrapperStyles: [],
  inputStyles: [],
  hasContent: false,
  placeholder: undefined,
  ignoreMarginBottom: false,
  size: undefined
};

export default Field;
